import { scopedSetter } from "../api/scopedSetter.js";
import { create } from "zustand";
import { api } from "../api/http.js";
import { useSession } from "../state/session.js";
import { toast } from "../ui/Toast.js";
import { t } from "../i18n/index.js";
import { errorText } from "../base/state.js";
import { arenaApi, battlesApi, expeditionsApi, unitsApi, workshopApi } from "./api.js";
import type { ArenaLogEntry, ArenaState, BannerView, BattleListEntry, BattleOutcome, ExpeditionState, Loot, PullView, RosterState } from "./types.js";

export type UnitsTab = "roster" | "squads" | "workshop" | "repair" | "armory" | "collection";
export const UNITS_TABS: UnitsTab[] = ["roster", "squads", "workshop", "repair", "armory", "collection"];

export interface RevealState {
  pulls: PullView[];
  banner: BannerView;
}

export interface TrainingState {
  xpPerHour: number;
  pooled: number;
  capHours: number;
  items: number;
}

/** A finished battle waiting to be shown: the results screen decides between replay and summary. */
export interface PendingResult {
  outcome: BattleOutcome;
  context: string;
  extra?: { stars?: number; firstClear?: boolean; ratingDelta?: number; rating?: number; coins?: number };
}

interface UnitsStore {
  roster: RosterState | null;
  banners: BannerView[] | null;
  arena: ArenaState | null;
  arenaLog: ArenaLogEntry[] | null;
  expeditions: ExpeditionState | null;
  battles: BattleListEntry[] | null;
  selected: string | null;
  squadSlot: number;
  reveal: RevealState | null;
  /** How many modules the draw summary's one-time dressing equipped; null until used (D69). Lives here so a re-render of the summary keeps the outcome. */
  revealDressed: number | null;
  /** True while the one-time dressing runs (one request per unit). */
  revealDressing: boolean;
  result: PendingResult | null;
  lastLoot: Loot | null;
  training: TrainingState | null;
  refreshTraining(): Promise<void>;
  refreshRoster(): Promise<void>;
  refreshWorkshop(): Promise<void>;
  refreshArena(): Promise<void>;
  refreshExpeditions(): Promise<void>;
  refreshBattles(): Promise<void>;
  refreshMe(): Promise<void>;
  select(rowId: string | null): void;
  /** Fills the unit's empty slots with the best free modules and reports how many (D69). */
  /** Dresses one unit with the best free modules; resolves to how many modules were equipped. */
  autoEquip(rowId: string): Promise<number>;
  /** Puts a depot module on the best unit with that slot free (D69). */
  autoEquipModule(moduleRowId: string): Promise<boolean>;
  setSquadSlot(slot: number): void;
  setReveal(reveal: RevealState | null): void;
  setRevealDressed(n: number | null): void;
  setRevealDressing(on: boolean): void;
  setResult(result: PendingResult | null): void;
  /** Runs a mutation and refreshes roster and account state afterwards. */
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

export const useUnits = create<UnitsStore>((set, get) => ({
  roster: null,
  banners: null,
  arena: null,
  arenaLog: null,
  expeditions: null,
  battles: null,
  selected: null,
  squadSlot: 1,
  reveal: null,
  revealDressed: null,
  revealDressing: false,
  result: null,
  lastLoot: null,
  training: null,
  refreshTraining: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      set({ training: await api<TrainingState>("GET", "/api/units/training") });
    } catch {
      set({ training: null });
    }
  },
  refreshRoster: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      set({ roster: await unitsApi.roster() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshWorkshop: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      set({ banners: await workshopApi.banners() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshArena: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      const [arena, arenaLog] = await Promise.all([arenaApi.state(), arenaApi.log()]);
      set({ arena, arenaLog });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshExpeditions: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      set({ expeditions: await expeditionsApi.state() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshBattles: async () => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      set({ battles: await battlesApi.list() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshMe: async () => {
    try {
      await useSession.getState().load();
    } catch {
      /* the next request refreshes */
    }
  },
  select: (selected) => set({ selected }),
  autoEquip: async (rowId) => {
    const res = await get().run(() => unitsApi.autoEquip(rowId));
    if (!res) return 0;
    if (res.equipped.length > 0) toast.success(t("units.autoEquip.done", { n: res.equipped.length }));
    else toast.info(t("units.autoEquip.none"));
    await get().refreshRoster();
    return res.equipped.length;
  },
  autoEquipModule: async (moduleRowId) => {
    const res = await get().run(() => unitsApi.autoEquipModule(moduleRowId));
    if (!res) return false;
    if (res.equipped) toast.success(t("units.autoEquip.done", { n: 1 }));
    else toast.info(t("units.autoEquip.none"));
    await get().refreshRoster();
    return res.equipped;
  },
  setSquadSlot: (squadSlot) => set({ squadSlot }),
  setReveal: (reveal) => set({ reveal, revealDressed: null, revealDressing: false }),
  setRevealDressed: (revealDressed) => set({ revealDressed }),
  setRevealDressing: (revealDressing) => set({ revealDressing }),
  setResult: (result) => set({ result }),
  run: async (op, okText) => {
    const set = scopedSetter<UnitsStore>(useUnits.setState);
    try {
      const result = await op();
      if (!set.current()) return null;
      await Promise.all([get().refreshRoster(), get().refreshMe()]);
      if (!set.current()) return null;
      if (okText) toast.success(okText);
      return result;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
}));
